import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Building2, LayoutDashboard, Users, FileText,
  LogOut, UserCog, Menu, X, ChevronLeft, MapPinned, PlusSquare,
  MessageSquare, CreditCard, Phone, Image, Package, LifeBuoy, Home
} from 'lucide-react';
import bannerLogo from '../assets/banner.png';
import squareLogo from '../assets/borderized_logo.png';

const Sidebar = () => {
  const { logout, userProfile } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  // Close mobile sidebar on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const role = userProfile?.role;
  const navigationByRole = {
    admin: [
      { name: 'Dashboard', icon: LayoutDashboard, href: '/' },
      { name: 'Properties', icon: Building2, href: '/properties' },
      { name: 'Homestays', icon: Home, href: '/homestays' },
      { name: 'Add Property', icon: PlusSquare, href: '/add-property' },
      { name: 'Banners / Banner Ads', icon: Image, href: '/banners' },
      { name: 'Sellers / Users', icon: Users, href: '/sellers' },
      { name: 'Callback Requests', icon: Phone, href: '/leads' },
      { name: 'Conversations', icon: MessageSquare, href: '/chats' },
      { name: 'Transactions', icon: CreditCard, href: '/transactions' },
      { name: 'Plans', icon: Package, href: '/plans' },
      { name: 'Helpdesk', icon: LifeBuoy, href: '/helpdesk' },
      { name: 'Reports / Export', icon: FileText, href: '/reports' },
      { name: 'User Management', icon: UserCog, href: '/users' },
    ],
    data_entry: [
      { name: 'Dashboard', icon: LayoutDashboard, href: '/' },
      { name: 'Properties', icon: Building2, href: '/properties' },
      { name: 'Homestays', icon: Home, href: '/homestays' },
      { name: 'Add Property', icon: PlusSquare, href: '/add-property' },
      { name: 'Banners / Banner Ads', icon: Image, href: '/banners' },
      { name: 'Sellers / Users', icon: Users, href: '/sellers' },
      { name: 'Helpdesk', icon: LifeBuoy, href: '/helpdesk' },
    ],
    sales: [
      { name: 'Dashboard', icon: LayoutDashboard, href: '/' },
      { name: 'Properties', icon: Building2, href: '/properties' },
      { name: 'Homestays', icon: Home, href: '/homestays' },
      { name: 'Sellers / Users', icon: Users, href: '/sellers' },
      { name: 'Callback Requests', icon: Phone, href: '/leads' },
      { name: 'Conversations', icon: MessageSquare, href: '/chats' },
      { name: 'Transactions', icon: CreditCard, href: '/transactions' },
      { name: 'Helpdesk', icon: LifeBuoy, href: '/helpdesk' },
    ]
  };
  const roleLabel = {
    admin: 'Admin Panel',
    data_entry: 'Data Entry',
    sales: 'Sales Panel'
  };
  const navigation = navigationByRole[role] || navigationByRole.data_entry;

  const sidebarContent = (isMobile) => (
    <div
      className={`h-full min-h-full bg-white border-r border-slate-200 flex flex-col transition-all duration-200 ${isMobile ? 'w-72' : collapsed ? 'w-[68px]' : 'w-64'
        }`}
      data-testid="admin-sidebar"
    >
      {/* Header */}
      <div className={`p-4 border-b border-slate-200 ${collapsed && !isMobile ? 'flex justify-center' : ''}`}>
        <div className={`flex items-center ${collapsed && !isMobile ? '' : 'justify-between w-full'}`}>
          {(!collapsed || isMobile) && (
            <Link to="/" className="flex items-center gap-3 min-w-0">
              <img src={bannerLogo} alt="JoldiPabo" className="h-9 w-auto object-contain" />
            </Link>
          )}

          {isMobile ? (
            <button onClick={() => setMobileOpen(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="close-mobile-sidebar">
              <X className="w-5 h-5" />
            </button>
          ) : (
            <button onClick={() => setCollapsed(!collapsed)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-500 hidden lg:block" data-testid="collapse-sidebar-btn">
              {collapsed ? (
                <Menu className="w-5 h-5" />
              ) : (
                <ChevronLeft className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {navigation.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.name}
              to={item.href}
              title={collapsed && !isMobile ? item.name : undefined}
              className={`flex items-center gap-3 rounded-lg text-sm font-medium transition-colors ${collapsed && !isMobile ? 'px-2.5 py-2.5 justify-center' : 'px-3 py-2.5'
                } ${isActive
                  ? 'bg-orange-50 text-[#ee7923]'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              data-testid={`nav-${item.name.toLowerCase().replace(/[\s\/]/g, '-')}`}
            >
              <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-[#ee7923]' : 'text-slate-400'}`} />
              {(!collapsed || isMobile) && <span className="truncate">{item.name}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-slate-200">
        {(!collapsed || isMobile) && (
          <div className="mb-3 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Signed in as</p>
            <p className="text-sm font-medium text-slate-900 truncate">{userProfile?.name}</p>
            <p className="text-xs text-slate-500 truncate">{userProfile?.email}</p>
          </div>
        )}
        <Button
          variant="outline"
          className={`w-full text-slate-600 hover:text-slate-900 ${collapsed && !isMobile ? 'px-2 justify-center' : 'justify-start'}`}
          onClick={logout}
          data-testid="logout-button"
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut className={`w-4 h-4 ${collapsed && !isMobile ? '' : 'mr-2'}`} />
          {(!collapsed || isMobile) && 'Sign Out'}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-4 left-4 z-40 p-2 bg-white border border-slate-200 rounded-lg shadow-sm lg:hidden"
        data-testid="open-mobile-sidebar"
      >
        <Menu className="w-5 h-5 text-slate-700" />
      </button>

      {/* Mobile overlay + sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} data-testid="sidebar-overlay" />
          <div className="relative z-10 h-full">
            {sidebarContent(true)}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex shrink-0 self-stretch">
        {sidebarContent(false)}
      </div>
    </>
  );
};

export default Sidebar;
